import {
  type KeylessVehicle,
  type LockState,
  VEHICLE_COMMANDS,
  type VehicleCommand,
} from "@/hooks/useKeylessPortal"

interface VehicleControlsProps {
  vehicle: KeylessVehicle
  isSending: boolean
  isRefreshing: boolean
  onCommand: (command: VehicleCommand) => void
  onRefresh: () => void
  onSignOut: () => void
}

const STATUS_LABELS: Record<LockState, string> = {
  locked: "Locked",
  unlocked: "Unlocked",
  locking: "Locking…",
  unlocking: "Unlocking…",
  unknown: "Unknown",
}

/**
 * Only a settled state disables its own button. An in-flight or unknown state leaves both
 * available, so a vehicle that never confirms doesn't block the driver.
 */
const isCommandDisabled = (lockState: LockState, command: VehicleCommand) => {
  if (command === VEHICLE_COMMANDS.lock) return lockState === "locked"
  return lockState === "unlocked"
}

const VehicleControls = ({
  vehicle,
  isSending,
  isRefreshing,
  onCommand,
  onRefresh,
  onSignOut,
}: VehicleControlsProps) => {
  const statusLabel = STATUS_LABELS[vehicle.lockState]
  const isBusy = isSending || isRefreshing

  return (
    <div className="screen">
      <div className="vehicle-screen">
        <div className="driver-row">
          <div className="avatar">🚐</div>
          <div>
            <div className="driver-name">Vehicle access</div>
            <div className="driver-sub">{vehicle.licensePlate}</div>
          </div>
          <button className="logout-btn" onClick={onSignOut} type="button">
            Sign out
          </button>
        </div>

        <div className="vehicle-card">
          <div className="vehicle-header">
            <div>
              <div className="vehicle-plate">{vehicle.licensePlate}</div>
              <div className="vehicle-model">Electric van · HIVED fleet</div>
            </div>
            <div className={`status-pill status-${vehicle.lockState}`}>
              <div className={`status-dot status-dot-${vehicle.lockState}`} />
              <span>{statusLabel}</span>
            </div>
          </div>

          <div className="sky-bar" />

          <div className="stats">
            <div className="stat">
              <div className="stat-label">Fleet</div>
              <div className="stat-value">HIVED</div>
            </div>
            <div className="stat">
              <div className="stat-label">Battery</div>
              <div className="stat-value">
                {vehicle.batteryPercent === null ? "—" : `${Math.round(vehicle.batteryPercent)}%`}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Status</div>
              <div className="stat-value">{statusLabel}</div>
            </div>
          </div>

          <div className="controls">
            <button
              className="ctrl-btn"
              disabled={isCommandDisabled(vehicle.lockState, VEHICLE_COMMANDS.unlock) || isBusy}
              onClick={() => onCommand(VEHICLE_COMMANDS.unlock)}
              type="button"
            >
              🔓 Unlock
            </button>
            <button
              className="ctrl-btn"
              disabled={isCommandDisabled(vehicle.lockState, VEHICLE_COMMANDS.lock) || isBusy}
              onClick={() => onCommand(VEHICLE_COMMANDS.lock)}
              type="button"
            >
              🔒 Lock
            </button>
          </div>

          <button className="refresh-btn" disabled={isBusy} onClick={onRefresh} type="button">
            {isRefreshing ? "Refreshing…" : "↻ Refresh status"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VehicleControls
